import { useQuery } from '@tanstack/react-query'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import Switch from 'ext-ui/components/Switch/Switch'
import React, {
  FormEvent,
  ReactElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import BillingAddressFieldset from 'src/component-form/BillingAddressFieldset/BillingAddressFieldset'
import {
  BillingAddressErrors,
  billingAddressFieldsForCountry,
  BillingAddressValues,
  validateBillingAddress,
} from 'src/component-form/BillingAddressFieldset/billingAddressRules'
import ConfirmCardDetailsModal from 'src/component-modal/ConfirmCardDetailsModal/ConfirmCardDetailsModal'
import ConnectPhysicalCardModal from 'src/component-modal/ConnectPhysicalCardModal/ConnectPhysicalCardModal'
import DisconnectPhysicalCardModal from 'src/component-modal/DisconnectPhysicalCardModal/DisconnectPhysicalCardModal'
import { usePrevious } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  CreditCardDisplayStatus,
  CreditCardListModel,
  CreditCardStatus,
  PhysicalCardStatus,
} from 'src/models/creditCard.models'
import {
  countriesQueries,
  toCountryListModelMap,
} from 'src/queries/countries/countries.queries'
import {
  isActivePhysicalCardStatus,
  isRecoverableEnrollmentFailure,
} from 'src/queries/creditCard.derivations'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

import { settingsAnchors } from '../../settingsAnchors'
import SettingsControlRow from '../SettingsControlRow/SettingsControlRow'
import SettingsDisclosure from '../SettingsDisclosure/SettingsDisclosure'
import SettingsDrawerShell from '../SettingsDrawerShell/SettingsDrawerShell'
import SettingsTile from '../SettingsTile/SettingsTile'
import style from './CardSettingsTile.style'

/**
 * EX-40631 — the API-backed billing & contact form's values.
 *
 * `nameLine1`, `nameLine2` and `mobilePhone` are carried but NOT edited. The
 * 2026-08-11 design cleanup removed the "Official display name" and "Mobile
 * phone" controls from card settings, while the billing-contact PUT remains a
 * complete SET — so the form round-trips the server's current values untouched.
 * Dropping them from this type would send `null`/`''` and silently clear real
 * data on the next address save.
 *
 * `nameLine2` is `''` for absent — the page maps `''` ⇄ `null` at the transport
 * boundary, because a complete-set PUT distinguishes them.
 */
export type UpdatedBillingContact = {
  /** Carried through unedited; see the note above. */
  nameLine1: string
  /** Carried through unedited; see the note above. */
  nameLine2: string
  /** E.164, e.g. `+12125550143`. Carried through unedited. */
  mobilePhone: string
  address: BillingAddressValues
}

/** Per-group load state for the billing & contact read. */
export type BillingContactState = 'loading' | 'ready' | 'error'

/** Shape produced by `toCountryOptions` — resolved by the page, passed in. */
export type CountryOption = { value: string; details: { text: string } }

type CardSettingsTileProps = {
  /** Grid-column class from the bento parent. */
  className?: string
  creditCard: CreditCardListModel
  /** Localized country list, resolved by the page. */
  countryOptions: CountryOption[]
  /** `canUpdateCreditCard`. When false the billing disclosure is read-only. */
  canEditCardDetails: boolean
  /** `PHYSICAL_CARD_TRANSACTIONS_FEED` — required to *connect* the feed. */
  canConnectPhysicalCardFeed: boolean
  /** EX-40641 remote-config gate for the confirm-missing-details flow. */
  confirmCardDetailsEnabled?: boolean
  /** EX-40694 remote-config gate for the recoverable-failure retry surfaces. */
  confirmCardDetailsRetryEnabled?: boolean
  /** Brand gate for the connected-capabilities disclosure. */
  showConnectedCapabilities: boolean
  /** Capability reasonCode behind a denied edit; see BillingContactRow. */
  billingEditDeniedReason?: string
  billingContact?: UpdatedBillingContact
  billingContactState?: BillingContactState
  onSaveBillingContact?: (values: UpdatedBillingContact) => void
  /**
   * Fired once a billing save lands. The drawer closes on success, so this is
   * where the page raises its confirmation — the form has nowhere left to show
   * one.
   */
  onBillingContactSaved?: () => void
  isSavingBillingContact?: boolean
  /**
   * Server-authored failure message, not a boolean: the billing-contact family
   * has recoveries the user must be told apart — a stale version
   * (`CARD-MGMT.28`) means "refresh and retry", a validation failure means
   * "fix the field". Collapsing them to "try again" hides the difference.
   */
  billingContactSaveError?: string
  /** Fired after the physical-card feed enroll flow completes, so the page can
   *  refresh card state and surface its own confirmation. */
  onPhysicalCardFeedConnected?: () => void
  /** Fired when the enroll flow settled on NEEDS_CVS_VERIFICATION: 3DS passed but the EX-code
   *  step is still outstanding, so the card is NOT connected yet. The page should refresh card
   *  state (to render the EX-code prompt) and must not confirm activation (EX-40942). */
  onPhysicalCardFeedVerificationPending?: () => void
  /** Fired after the physical-card feed is disconnected. */
  onPhysicalCardFeedDisconnected?: () => void
}

type CapabilityKey = 'physicalFeed' | 'virtualCards'

/**
 * Placeholder for the window before the read resolves. Only ever paired with a
 * `loading` or `error` state, which render their own body — it exists so the
 * row's props stay non-optional rather than to be displayed.
 */
const EMPTY_BILLING_CONTACT: UpdatedBillingContact = {
  nameLine1: '',
  nameLine2: '',
  mobilePhone: '',
  address: {
    address1: '',
    address2: '',
    city: '',
    province: '',
    postal: '',
    country: '',
  },
}

function countryLabelFor(countryOptions: CountryOption[], iso: string): string {
  return (
    countryOptions.find((option) => option.value === iso)?.details.text ?? iso
  )
}

/**
 * "New York, NY 10001" — the locality line the design's collapsed summary shows
 * beside the country. Each part is optional because address rules vary by
 * country (`province` and `postal` are genuinely absent in some), so the parts
 * are joined rather than templated.
 */
function localityLine(address: BillingAddressValues): string {
  const cityAndProvince = [address.city, address.province]
    .filter((part) => part !== '')
    .join(', ')
  return [cityAndProvince, address.postal]
    .filter((part) => part !== '')
    .join(' ')
}

function BillingContactRow(props: {
  value: UpdatedBillingContact
  state: BillingContactState
  countryOptions: CountryOption[]
  canEdit: boolean
  /**
   * Why editing is denied, when it is. `ROLE_NOT_ALLOWED` is the only reason
   * that justifies "contact an Admin or Owner" — for any other reason a role
   * change fixes nothing, and that copy sends the reader on a wild goose chase.
   * Same defect reviewers caught on the MCC surface in PR #7391.
   */
  editDeniedReason?: string
  onSave?: (values: UpdatedBillingContact) => void
  /** Fired once a save lands, so the page can confirm it. */
  onSaved?: () => void
  isSaving: boolean
  saveError?: string
}): ReactElement {
  const {
    value,
    state,
    countryOptions,
    canEdit,
    editDeniedReason,
    onSave,
    onSaved,
    isSaving,
    saveError,
  } = props
  const translate = useTranslate()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { data: countriesByIso = {} } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryListModelMap,
  })

  const [draft, setDraft] = useState<UpdatedBillingContact>(value)
  const [errors, setErrors] = useState<BillingAddressErrors>({})
  const [drawerOpen, setDrawerOpen] = useState(false)
  const editTriggerRef = useRef<HTMLButtonElement>(null)

  /*
   * Held in a ref rather than an effect dependency. Callers are not required to
   * memoize it, and an unstable callback in the settle effect's deps re-fires
   * it on every render — the failure mode `src/CLAUDE.md` documents. The
   * effect's own isSaving-transition guard is what makes it fire once.
   */
  const onSavedRef = useRef(onSaved)
  useEffect(() => {
    onSavedRef.current = onSaved
  }, [onSaved])

  // Re-seed the form when the server snapshot changes identity — a successful
  // save writes the authoritative response into the cache, and the form must
  // show it (and stop reporting "Saved" for an older edit). Keyed on the
  // primitive fields, never on the object identity, so a refetch returning an
  // equal snapshot does not clobber in-progress typing.
  const snapshotKey = `${value.nameLine1}|${value.nameLine2}|${value.mobilePhone}|${Object.values(value.address).join('|')}`
  const previousSnapshotKey = usePrevious(snapshotKey)
  useEffect(() => {
    if (
      previousSnapshotKey !== undefined &&
      previousSnapshotKey !== snapshotKey
    ) {
      setDraft(value)
    }
    // NOTE: `value` is intentionally NOT a dependency. The primitive
    // `snapshotKey` above is the transition signal; depending on the object
    // would re-seed the form on every render and discard in-progress typing.
  }, [snapshotKey, previousSnapshotKey])

  // Binds the rail's submit button back to the form it lives outside of.
  const formId = useId()

  /*
   * Close the drawer once a save lands, and hand confirmation to the page.
   *
   * The drawer unmounts on close (react-modal drops its children), so an inline
   * "saved" note here would never be read — the page raises a toast instead,
   * the same way it already confirms card activation. A FAILED save keeps the
   * drawer open: `saveError` renders beside the action, where the user can fix
   * the field and retry without reopening anything.
   *
   * Focus returns to the trigger that opened the drawer, so a keyboard user is
   * not dropped at the top of the document — matching the merchant-categories
   * drawer's own close behaviour.
   *
   * The drawer can be dismissed while a save is still in flight, so the two
   * halves are gated differently: the confirmation always fires, because the
   * save genuinely succeeded and the toast is the only signal the user gets,
   * while closing and restoring focus only apply to a drawer that is still
   * open — otherwise settling would yank focus back to a trigger the user has
   * already moved away from.
   */
  const prevSaving = usePrevious(isSaving)
  useEffect(() => {
    if (prevSaving && !isSaving && saveError === undefined) {
      onSavedRef.current?.()
      if (drawerOpen) {
        setDrawerOpen(false)
        editTriggerRef.current?.focus()
      }
    }
  }, [drawerOpen, prevSaving, isSaving, saveError])

  // Design summary is `locality · country` — it was `country · phone` until the
  // 2026-08-11 cleanup removed the phone from this surface entirely.
  //
  // Reads `value`, not `draft`: this is a readout of what the server holds. The
  // form moved behind a dismissible drawer, so a draft abandoned by closing it
  // would otherwise keep presenting an uncommitted edit as the card's current
  // address until the next reload. See summaryRows below for the same reason.
  const summary = [
    localityLine(value.address),
    countryLabelFor(countryOptions, value.address.country),
  ]
    .filter((part) => part !== '')
    .join(' · ')

  if (state === 'loading') {
    return (
      <SettingsDisclosure
        title={translate('Billing address')}
        summary={translate('Loading')}
        actionLabel={translate('View')}
      >
        <p
          className="CardSettingsTile-readOnlyNote"
          data-testid="billing-contact-loading"
        >
          {translate('Loading billing and contact details…')}
        </p>
      </SettingsDisclosure>
    )
  }

  if (state === 'error') {
    // A failed READ is not an empty form. Rendering blank inputs here would
    // invite the user to overwrite real server values with nothing, since the
    // PUT replaces the complete set.
    return (
      <SettingsDisclosure
        title={translate('Billing address')}
        summary={translate('Unavailable')}
        actionLabel={translate('View')}
      >
        <p
          className="CardSettingsTile-errorNote"
          role="alert"
          data-testid="billing-contact-load-error"
        >
          {translate(
            "We couldn't load billing and contact details. Refresh to try again.",
          )}
        </p>
      </SettingsDisclosure>
    )
  }

  const setAddress = (address: BillingAddressValues): void => {
    setDraft((current) => ({ ...current, address }))
    // Clear the address errors too. `setName` already does this per field;
    // without it a corrected City kept showing "City is required" until the
    // next submit recomputed the whole set.
    setErrors((current) => ({
      ...current,
      address1: undefined,
      address2: undefined,
      city: undefined,
      province: undefined,
      postal: undefined,
      country: undefined,
    }))
  }

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault()

    // "Rules unknown" is not "no rules". `validateBillingAddress` correctly
    // returns {} for an empty rule set so a pending catalogue cannot invent
    // errors — but the CALLER must not read that {} as "valid". Submitting
    // here would also carry the previous country's province/postal into a
    // complete-set PUT, because `clearUnusedBillingAddressFields` no-ops on an
    // empty rule set too. The server rejects that with no field on screen to
    // explain it.
    const addressFields = billingAddressFieldsForCountry(
      countriesByIso,
      draft.address.country,
    )
    if (draft.address.country !== '' && addressFields.length === 0) {
      setErrors({
        country: translate(
          'Still loading address rules for this country. Try again in a moment.',
        ),
      })
      return
    }

    const addressErrors = validateBillingAddress(draft.address, addressFields, {
      required: (label) => translate('${label} is required', { label }),
      invalid: (label) => translate('${label} is not valid', { label }),
      countryLabel: translate('Country'),
    })
    setErrors(addressErrors)
    if (Object.values(addressErrors).some((error) => error !== undefined)) {
      return
    }
    // `draft` still carries the server's nameLine1/nameLine2/mobilePhone
    // untouched — the PUT is a complete SET, so they must go back exactly as
    // they came even though this form no longer edits them.
    onSave?.(draft)
  }

  if (!canEdit) {
    const rows: { label: string; value: string }[] = [
      {
        label: translate('Country'),
        value: countryLabelFor(countryOptions, value.address.country),
      },
      { label: translate('Street address'), value: value.address.address1 },
      ...(value.address.address2
        ? [
            {
              label: translate('Apt, suite, etc.'),
              value: value.address.address2,
            },
          ]
        : []),
      { label: translate('City'), value: value.address.city },
      ...(value.address.province
        ? [{ label: translate('State'), value: value.address.province }]
        : []),
      ...(value.address.postal
        ? [{ label: translate('ZIP'), value: value.address.postal }]
        : []),
    ]

    return (
      <SettingsDisclosure
        title={translate('Billing address')}
        summary={summary}
        actionLabel={translate('View')}
      >
        <p
          className="CardSettingsTile-readOnlyNote"
          data-testid="billing-contact-read-only"
        >
          {editDeniedReason === 'ROLE_NOT_ALLOWED'
            ? translate(
                'You do not have permission to edit this account. Contact an Admin or Owner to make changes.',
              )
            : translate(
                'Editing billing details is not available for this card right now.',
              )}
        </p>
        <dl className="CardSettingsTile-kvList">
          {rows.map((row) => (
            <div key={row.label} className="CardSettingsTile-kv">
              <dt className="CardSettingsTile-kvLabel">{row.label}</dt>
              <dd className="CardSettingsTile-kvValue">{row.value}</dd>
            </div>
          ))}
        </dl>
      </SettingsDisclosure>
    )
  }

  // The complete address as read-only KV rows, shown once the disclosure is
  // expanded. Deliberately a DIFFERENT shape than the read-only-permission
  // variant above (Country / Street / Apt / City / State / ZIP as six rows):
  // that variant is unmodified per the design VQA, while this one follows the
  // "Street / City, state & ZIP / Country" grouping the design specifies for
  // the editable path.
  //
  // Reads `value`, not `draft`: these rows are a readout of the server's
  // address, while `draft` is the form's working copy. Closing the drawer
  // without saving does not reset `draft` (the re-seed effect above keys on the
  // SERVER snapshot, so that it never clobbers in-progress typing), so echoing
  // `draft` here would leave an abandoned edit on the page presenting itself as
  // the card's current billing address until the next reload.
  const summaryRows: { label: string; value: string }[] = [
    {
      label: translate('Street address'),
      value: [value.address.address1, value.address.address2]
        .filter((part) => part !== '')
        .join(', '),
    },
    {
      label: translate('City, state & ZIP'),
      value: localityLine(value.address),
    },
    {
      label: translate('Country'),
      value: countryLabelFor(countryOptions, value.address.country),
    },
  ]

  // Extracted so the drawer renders the SAME form rather than a duplicate —
  // editing still happens here; expanding the disclosure alone no longer does.
  const form = (
    <form
      id={formId}
      className="CardSettingsTile-form"
      onSubmit={handleSubmit}
      noValidate
    >
      <BillingAddressFieldset
        legend={translate('Billing address')}
        idPrefix="billing-contact-address"
        value={draft.address}
        onChange={setAddress}
        errors={errors}
      />
    </form>
  )

  /*
   * The address form has a real submit step, so its primary action sits in the
   * drawer's action rail — after the content card, flowing with the form — and
   * is bound back to the form with `form=` so Enter and click both still run
   * `handleSubmit` across that DOM distance. Validation, busy, saved and error
   * feedback stay local to the form's own action, not the tile's.
   */
  const formActions = (
    <div className="CardSettingsTile-formActions">
      {saveError !== undefined && (
        <span
          className="CardSettingsTile-errorNote"
          role="alert"
          data-testid="billing-contact-save-error"
        >
          {saveError}
        </span>
      )}
      <Button
        type="submit"
        form={formId}
        dropShadow={false}
        loading={isSaving}
        data-testid="billing-contact-save"
      >
        {translate('Save details')}
      </Button>
    </div>
  )

  return (
    <SettingsDisclosure
      title={translate('Billing address')}
      summary={summary}
      actionLabel={translate('Edit')}
    >
      <dl className="CardSettingsTile-kvList">
        {summaryRows.map((row) => (
          <div key={row.label} className="CardSettingsTile-kv">
            <dt className="CardSettingsTile-kvLabel">{row.label}</dt>
            <dd className="CardSettingsTile-kvValue">{row.value}</dd>
          </div>
        ))}
      </dl>
      <Button
        ref={editTriggerRef}
        className="CardSettingsTile-editTrigger"
        variant="secondary"
        dropShadow={false}
        onClick={() => setDrawerOpen(true)}
        data-testid="edit-billing-address"
      >
        {translate('Edit billing address')}
      </Button>
      <SettingsDrawerShell
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={translate('Billing address')}
        lead={translate(
          'Where statements and replacement cards are sent. Changes are logged in Activity.',
        )}
        footnote={translate(
          'Address changes are validated with the issuer before they take effect.',
        )}
        /* Carries the tile's own styles: the rail renders outside the content
           card, so the note classes would otherwise fall out of scope. */
        action={<div css={style}>{formActions}</div>}
      >
        <div className="CardSettingsTile-drawerContent" css={style}>
          {form}
        </div>
      </SettingsDrawerShell>
    </SettingsDisclosure>
  )
}

/**
 * Billing address without `canUpdateCreditCard` — the legacy
 * AccountBillingInformationBox drops its edit affordance entirely in this case,
 * so offering a form (and a Save that the API would reject) would be wrong.
 */
type CapabilityRow = {
  key: CapabilityKey
  title: string
  description: string
  connected: boolean
  disabled: boolean
  onToggle: (checked: boolean) => void
}

function CapabilitiesRow(props: {
  creditCard: CreditCardListModel
  canConnectPhysicalCardFeed: boolean
  confirmCardDetailsEnabled: boolean
  confirmCardDetailsRetryEnabled: boolean
  onConnected?: () => void
  onVerificationPending?: () => void
  onDisconnected?: () => void
}): ReactElement | null {
  const {
    creditCard,
    canConnectPhysicalCardFeed,
    confirmCardDetailsEnabled,
    confirmCardDetailsRetryEnabled,
    onConnected,
    onVerificationPending,
    onDisconnected,
  } = props
  const translate = useTranslate()

  const physicalStatus = creditCard.physicalCard?.status ?? null
  // Flag off = nothing confirmable: the status=null carve-out and the
  // confirm-details modal routing both collapse to pre-EX-40639 behavior.
  const missingCardDetails = confirmCardDetailsEnabled
    ? (creditCard.physicalCard?.missingCardDetails ?? [])
    : []
  // EX-40694: a recoverable enrollment failure (NOT_APPLICABLE + a bad-CVC / unknown-failure reason)
  // with confirmable missing details is the retry path, not a terminal state. Gated on its own
  // retry flag (a fast-follow kill switch — the parent flag was already live in prod) + non-empty
  // missingCardDetails + canConnectPhysicalCardFeed, so a flipped-off flag, an old backend ([]
  // here), or a user who can't act keeps the row hidden exactly as today.
  const isRecoverableConfirmable =
    confirmCardDetailsRetryEnabled &&
    isRecoverableEnrollmentFailure({
      status: physicalStatus,
      statusReason: creditCard.physicalCard?.statusReason ?? null,
    }) &&
    missingCardDetails.length > 0 &&
    canConnectPhysicalCardFeed
  // The card only has the physical-card feed capability when a physical card
  // exists and isn't closed — mirrors PhysicalCardTransactionsPanel's gating.
  // A null status normally means "not eligible right now", except when the
  // card is confirmable (missing card details): the API deliberately returns
  // status=null rather than ENROLLMENT_AVAILABLE for those cards, so this
  // carve-out is required or the row never renders for the population
  // EX-40639 exists to serve. Requires canConnectPhysicalCardFeed too, same as
  // PhysicalCardTransactionsPanel/AccountSettings — without it, a user who
  // can't act on the feed yet would see a permanently-disabled switch instead
  // of the row just staying hidden. NotApplicable normally hides the row, but a
  // recoverable-failed card (EX-40694) keeps it so the toggle can offer the retry.
  const hasPhysicalCapability =
    (physicalStatus !== null ||
      (missingCardDetails.length > 0 && canConnectPhysicalCardFeed)) &&
    (physicalStatus !== PhysicalCardStatus.NotApplicable ||
      isRecoverableConfirmable) &&
    physicalStatus !== PhysicalCardStatus.Closed
  const virtualCardsSupported = Boolean(
    creditCard.partner?.virtualCardsSupported,
  )

  // A card only sees a capability it actually has; `disabled` still reflects
  // whether that capability is operable right now. A closed card
  // (displayStatus === Closed) exposes neither.
  const isCardClosed =
    creditCard.displayStatus === CreditCardDisplayStatus.Closed

  // physicalFeed ← PhysicalCardTransactionsPanel: needs a (non-closed) physical
  // card, and also hides on a closed card even when the physical card's own
  // status hasn't flipped to Closed — the legacy panel returns null on
  // displayStatus === Closed. Without this a virtual-only account would surface a
  // permanently disabled physical-feed switch and a closed card an operable one.
  const showPhysicalFeedRow = hasPhysicalCapability && !isCardClosed

  // virtualCards ← extend-ios AccountSettingsPresenter.configureVirtualCardStack:
  // show when the card is not closed, the partner supports virtual cards, and the
  // physical status is not explicitly NotApplicable. A null/absent physical
  // status still shows — virtual-card support does not depend on holding a
  // physical card. The prior `isVirtualAccount || hasPhysicalCapability` gate
  // wrongly hid the row for VC-supported cards that have no physical card yet.
  const showVirtualCardsRow =
    !isCardClosed &&
    virtualCardsSupported &&
    physicalStatus !== PhysicalCardStatus.NotApplicable

  const [physicalFeedConnected, setPhysicalFeedConnected] = useState(
    physicalStatus === PhysicalCardStatus.Active,
  )
  const [showConnectModal, setShowConnectModal] = useState(false)
  const [showConfirmDetailsModal, setShowConfirmDetailsModal] = useState(false)
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)

  // The switch is operable only when the card actually has a physical card
  // AND — for *connecting* — the user holds the PHYSICAL_CARD_TRANSACTIONS_FEED
  // entitlement. An already-connected feed stays operable without it so the
  // user can still disconnect, matching PhysicalCardTransactionsPanel's escape
  // hatch. Without this the enroll flow would be reachable by anyone.
  const physicalDisabled =
    !hasPhysicalCapability ||
    (!physicalFeedConnected && !canConnectPhysicalCardFeed)

  // Virtual cards are READ-ONLY here. There is no enable/disconnect mutation
  // wired for this capability, so the switch reflects the card's real state
  // (mirroring VirtualCardCapabilitiesPanel.determineInitialConnectedStatus)
  // and is always disabled — toggling it would otherwise report a capability
  // as enabled when nothing was actually changed.
  const virtualCardsConnected =
    virtualCardsSupported &&
    creditCard.partner?.status === CreditCardStatus.Active

  const [drawerOpen, setDrawerOpen] = useState(false)

  // Turning a capability OFF routes through a confirm modal
  // (`DisconnectPhysicalCardModal`). A confirm modal opening on top of an open
  // drawer is two stacked overlays with competing focus traps, so the drawer
  // closes first and the decision continues on the page — the tile summary
  // behind it updates once the modal resolves. Turning a capability ON is not
  // a confirmation (it starts an enrollment flow instead) and does not close
  // the drawer.
  const askOff = (which: CapabilityKey): void => {
    setDrawerOpen(false)
    if (which === 'physicalFeed') {
      setShowDisconnectModal(true)
    }
  }

  const handlePhysicalToggle = (checked: boolean): void => {
    if (physicalDisabled) return
    if (checked) {
      if (missingCardDetails.length > 0) {
        setShowConfirmDetailsModal(true)
        return
      }
      setShowConnectModal(true)
    } else {
      askOff('physicalFeed')
    }
  }

  // Memoized so PhysicalCardVerifyStep's Pusher-subscription and timeout effects (both depend
  // on these callbacks) don't tear down and re-arm on every render — see the identical pattern
  // in PhysicalCardTransactionsPanel.
  const handlePhysicalEnrollmentComplete = useCallback(
    (_cardId: string, physicalCardStatus?: PhysicalCardStatus): void => {
      setIsVerifying(false)
      setShowConnectModal(false)
      setShowConfirmDetailsModal(false)
      // EX-40942: NEEDS_CVS_VERIFICATION is "3DS passed, EX code still outstanding", not
      // connected. Flipping the switch on and toasting here was the false success in the bug
      // report. Only an explicit ACTIVE counts; an absent or unknown status is not assumed
      // connected either — both just ask the page to refresh its card state.
      if (!isActivePhysicalCardStatus(physicalCardStatus)) {
        onVerificationPending?.()
        return
      }
      setPhysicalFeedConnected(true)
      onConnected?.()
    },
    [onConnected, onVerificationPending],
  )

  const handleVerifyStart = useCallback((): void => {
    setIsVerifying(true)
  }, [])

  const handleVerifyError = useCallback((): void => {
    setIsVerifying(false)
  }, [])

  // No-op: the virtual cards row is read-only until an enable flow exists.
  const handleVirtualCardsToggle = (): void => undefined

  const rows: CapabilityRow[] = []
  if (showPhysicalFeedRow) {
    rows.push({
      key: 'physicalFeed',
      title: translate('Physical card feed'),
      description: translate(
        'Import physical card transactions into the activity and transaction views.',
      ),
      connected: physicalFeedConnected,
      disabled: physicalDisabled,
      onToggle: handlePhysicalToggle,
    })
  }
  if (showVirtualCardsRow) {
    rows.push({
      key: 'virtualCards',
      title: translate('Virtual cards'),
      description: translate(
        'Allow virtual cards to be created on this physical card — to control spend, reduce risk, and simplify vendor payments.',
      ),
      connected: virtualCardsConnected,
      disabled: true,
      onToggle: handleVirtualCardsToggle,
    })
  }

  // Neither capability applies to this card type — nothing to disclose, matching
  // both legacy panels returning null.
  if (rows.length === 0) {
    return null
  }

  // Every switch in the drawer is disabled — opening it would show the user a
  // panel of controls none of which they can operate. The rows with their state
  // pills already render inline, so the disclosure still says everything the
  // drawer would; the button is the only thing with nothing to offer.
  const hasOperableCapability = rows.some((row) => !row.disabled)

  const summary = [
    ...(showPhysicalFeedRow
      ? [
          `${translate('Physical card feed')} ${
            physicalFeedConnected ? translate('on') : translate('off')
          }`,
        ]
      : []),
    ...(showVirtualCardsRow
      ? [
          `${translate('Virtual cards')} ${
            virtualCardsConnected ? translate('on') : translate('off')
          }`,
        ]
      : []),
  ].join(' · ')

  const renderCapabilityRow = (
    row: CapabilityRow,
    withSwitch: boolean,
  ): ReactElement => (
    <SettingsControlRow
      key={row.key}
      title={row.title}
      description={row.description}
    >
      <Pill size="small" color={row.connected ? 'highlight' : 'pending'}>
        {row.connected ? translate('Connected') : translate('Disconnected')}
      </Pill>
      {withSwitch && (
        <Switch
          checked={row.connected}
          disabled={row.disabled}
          ariaLabel={row.title}
          onCheckedChange={row.onToggle}
          data-testid={`capability-${row.key}`}
        />
      )}
    </SettingsControlRow>
  )

  return (
    <SettingsDisclosure
      title={translate('Connected capabilities')}
      actionLabel={translate('Manage')}
      summary={summary}
    >
      {rows.map((row) => renderCapabilityRow(row, false))}
      {hasOperableCapability && (
        <Button
          className="CardSettingsTile-editTrigger"
          variant="secondary"
          dropShadow={false}
          onClick={() => setDrawerOpen(true)}
          data-testid="manage-connected-capabilities"
        >
          {translate('Manage Connect capabilities')}
        </Button>
      )}

      <SettingsDrawerShell
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={translate('Connect capabilities')}
        lead={translate(
          'What this physical card is connected to. Turning a capability off is confirmed before it applies.',
        )}
        footnote={translate(
          'Turning off virtual cards pauses the ones that already exist. The physical card feed can be reconnected at any time.',
        )}
      >
        <div className="CardSettingsTile-drawerContent" css={style}>
          {rows.map((row) => renderCapabilityRow(row, true))}
        </div>
      </SettingsDrawerShell>

      <ConnectPhysicalCardModal
        isOpen={showConnectModal}
        onClose={() => {
          if (isVerifying) {
            return
          }
          setShowConnectModal(false)
        }}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        reviewEnrollCardProps={{
          onBack: () => setShowConnectModal(false),
          creditCardId: creditCard.id,
          onVerifyStart: handleVerifyStart,
          onVerifyComplete: handlePhysicalEnrollmentComplete,
          onVerifyError: handleVerifyError,
          submissionData: {
            binData: {
              id: creditCard.issuerId,
              eligibility: 'PHYSICAL_ONLY',
              issuerLogoImg: creditCard.cardImage.urls.small,
              issuerRequiresVerification: false,
            },
            addressData: {
              cardName: creditCard.displayName,
              ...creditCard.address,
            },
            cardData: {
              number: `**** **** **** ${creditCard.last4}`,
              cvc: '***',
              expiration: creditCard.expiresFormattedString,
            },
          },
        }}
      />
      <ConfirmCardDetailsModal
        isOpen={showConfirmDetailsModal}
        onClose={() => {
          if (isVerifying) {
            return
          }
          setShowConfirmDetailsModal(false)
        }}
        creditCardId={creditCard.id}
        last4={creditCard.last4}
        onVerifyStart={handleVerifyStart}
        onVerifyComplete={handlePhysicalEnrollmentComplete}
        onVerifyError={handleVerifyError}
      />
      <DisconnectPhysicalCardModal
        isOpen={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onDisconnectComplete={() => {
          setShowDisconnectModal(false)
          setPhysicalFeedConnected(false)
          onDisconnected?.()
        }}
        creditCardId={creditCard.id}
      />
    </SettingsDisclosure>
  )
}

function IssuerDetailsRow(props: {
  creditCard: CreditCardListModel
}): ReactElement {
  const { creditCard } = props
  const translate = useTranslate()

  // EX-40628 relabels these rows "BIN" and "Network" per the design, and its
  // "network or issuer name" wording accepts `issuerName` as the value.
  //
  // KNOWN GAP, decided deliberately rather than overlooked: the credit card
  // model carries neither field. There is no BIN (only `last4`, which is the
  // opposite end of the PAN), and `issuerName` is the issuing bank, not the
  // network. So "BIN" labels a last-four and "Network" labels a bank until a
  // real source exists — raised on the ticket, not silently absorbed here.
  const rows: { label: string; value: string }[] = [
    { label: translate('BIN'), value: `•••• ${creditCard.last4}` },
    { label: translate('Network'), value: creditCard.issuerName },
    {
      label: translate('Expiration'),
      value: creditCard.expiresFormattedString,
    },
  ]

  return (
    <SettingsDisclosure
      title={translate('Issuer details')}
      summary={translate('Read-only')}
      actionLabel={translate('View')}
    >
      <dl className="CardSettingsTile-kvList">
        {rows.map((row) => (
          <div key={row.label} className="CardSettingsTile-kv">
            <dt className="CardSettingsTile-kvLabel">{row.label}</dt>
            <dd className="CardSettingsTile-kvValue">{row.value}</dd>
          </div>
        ))}
      </dl>
    </SettingsDisclosure>
  )
}

/**
 * "Card settings" tile (EX-40354) — billing address, connected capabilities,
 * and read-only issuer details. Props-only: the page resolves the country list,
 * the permission/entitlement/brand gates, and the write, and owns the cache
 * invalidation + confirmation toast for the capability flows.
 */
export default function CardSettingsTile(
  props: CardSettingsTileProps,
): ReactElement {
  const {
    className,
    creditCard,
    countryOptions,
    canEditCardDetails,
    canConnectPhysicalCardFeed,
    confirmCardDetailsEnabled = true,
    confirmCardDetailsRetryEnabled = true,
    showConnectedCapabilities,
    billingEditDeniedReason,
    billingContact,
    billingContactState = 'loading',
    onSaveBillingContact,
    onBillingContactSaved,
    isSavingBillingContact = false,
    billingContactSaveError,
    onPhysicalCardFeedConnected,
    onPhysicalCardFeedVerificationPending,
    onPhysicalCardFeedDisconnected,
  } = props
  const translate = useTranslate()

  // EX-40631. `billingContact` is only absent before the read resolves, so a
  // ready state without it is treated as loading rather than rendered as an
  // empty form the user could save over real values.
  const resolvedBillingContactState: BillingContactState =
    billingContactState === 'ready' && billingContact === undefined
      ? 'loading'
      : billingContactState

  return (
    <SettingsTile
      className={className}
      title={translate('Card settings')}
      id={settingsAnchors.cardSettings}
    >
      <div className="CardSettingsTile" css={style}>
        {/*
          ONE billing form, always the design's. Earlier this fell back to the
          legacy card-backed row when the billing-contact capability was denied,
          which put "Display Name"/"Company Name" on the redesigned page — labels
          the design does not have. The DATA SOURCE degrades, the UI does not.
        */}
        <BillingContactRow
          value={billingContact ?? EMPTY_BILLING_CONTACT}
          state={resolvedBillingContactState}
          countryOptions={countryOptions}
          canEdit={canEditCardDetails}
          editDeniedReason={billingEditDeniedReason}
          onSave={onSaveBillingContact}
          onSaved={onBillingContactSaved}
          isSaving={isSavingBillingContact}
          saveError={billingContactSaveError}
        />
        {showConnectedCapabilities && (
          <CapabilitiesRow
            creditCard={creditCard}
            canConnectPhysicalCardFeed={canConnectPhysicalCardFeed}
            confirmCardDetailsEnabled={confirmCardDetailsEnabled}
            confirmCardDetailsRetryEnabled={confirmCardDetailsRetryEnabled}
            onConnected={onPhysicalCardFeedConnected}
            onVerificationPending={onPhysicalCardFeedVerificationPending}
            onDisconnected={onPhysicalCardFeedDisconnected}
          />
        )}
        <IssuerDetailsRow creditCard={creditCard} />
      </div>
    </SettingsTile>
  )
}
